import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Label } from './ui/label';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Input } from './ui/input';
import { Separator } from './ui/separator';
import { woodJointOptions } from '../data/mockData';
import ColorPicker from './ColorPicker';
import { useTranslation } from '../context/TranslationContext';

const TerrasdeurOptionsPanel = ({ config, onConfigChange }) => {
  const { t } = useTranslation();
  
  // Helper function to translate glass texture and glazing type names
  const translateGlassTextureName = (id) => {
    const translations = {
      'helder-glas': t('options.clearGlass'),
      'chinchilla': t('options.chinchilla'),
      'satinato': t('options.satinato')
    };
    return translations[id] || id;
  };
  
  const translateGlassTextureDescription = (id) => {
    const translations = {
      'helder-glas': t('options.clearGlassDesc'),
      'chinchilla': t('options.chinchillaDesc'),
      'satinato': t('options.satinatoDesc')
    };
    return translations[id] || '';
  };
  
  const translateGlazingTypeName = (id) => {
    const translations = {
      'double': t('options.doubleGlass24'),
      'triple': t('options.doubleGlass36'),
      'double-24': t('options.doubleGlass24'),
      'double-36': t('options.doubleGlass36')
    };
    return translations[id] || id;
  };
  
  const translateGlazingTypeDescription = (id) => {
    const translations = {
      'double': t('options.doubleGlass24Desc'),
      'triple': t('options.doubleGlass36Desc'),
      'double-24': t('options.doubleGlass24Desc'),
      'double-36': t('options.doubleGlass36Desc')
    };
    return translations[id] || '';
  };
  
  // Helper function to translate option names (like wood joint)
  const translateOptionName = (id, name) => {
    const translations = {
      'joint-45': t('options.woodJoint45'),
      'joint-90': t('options.woodJoint90')
    };
    return translations[id] || name;
  };
  
  // Local dimension inputs so drawing doesn't react until user finishes editing
  const [widthInput, setWidthInput] = useState(String(config.width));
  const [heightInput, setHeightInput] = useState(String(config.height));

  // Keep local inputs in sync when config is changed externally
  useEffect(() => {
    setWidthInput(String(config.width));
  }, [config.width]);

  useEffect(() => {
    setHeightInput(String(config.height));
  }, [config.height]);

  // Dimension constraints
  const MIN_DIMENSION = 1500;
  const MAX_DIMENSION = 2500;

  // On blur / enter, enforce the 1500–2500 constraints and update global config once
  const commitDimension = (field, rawValue) => {
    const parsed = parseInt(rawValue, 10);
    if (Number.isNaN(parsed)) {
      // Reset local input back to current config value if invalid/empty
      const fallback = String(config[field]);
      if (field === 'width') setWidthInput(fallback);
      if (field === 'height') setHeightInput(fallback);
      return;
    }
    const clamped = Math.max(MIN_DIMENSION, Math.min(MAX_DIMENSION, parsed));
    onConfigChange(field, clamped);
  };
  const designOptions = [
    { id: 'no-panel', name: t('options.noPanel'), description: t('options.noPanelDesc'), image: process.env.PUBLIC_URL + '/images/no-panel.png' },
    { id: 'left-panel', name: t('options.leftPanel'), description: t('options.leftPanelDesc'), image: process.env.PUBLIC_URL + '/images/left-panel.png' },
    { id: 'right-panel', name: t('options.rightPanel'), description: t('options.rightPanelDesc'), image: process.env.PUBLIC_URL + '/images/right-panel.png' },
    { id: 'both-panels', name: t('options.bothPanels'), description: t('options.bothPanelsDesc'), image: process.env.PUBLIC_URL + '/images/both-panel.png' }
  ];

  const doorTypes = [
    { id: 'normal', name: t('options.normal') },
    { id: 'top-light', name: t('options.topLight') }
  ];

  const profileSystems = [
    { 
      id: 'synego-1', 
      name: t('options.recessedProfile'),
      description: t('options.recessedProfileDesc'),
      logo: process.env.PUBLIC_URL + '/images/synego.jpg',
      color: '#E30613'
    },
    { 
      id: 'option-2', 
      name: t('options.withoutStop'),
      description: t('options.withoutStopDesc'),
      logo: process.env.PUBLIC_URL + '/images/Geen-aanslag-2-300x260.jpg',
      color: '#666'
    },
    { 
      id: 'option-3', 
      name: t('options.stopLeftTopRight'),
      description: t('options.stopLeftTopRightDesc'),
      logo: process.env.PUBLIC_URL + '/images/Wel-aanslag-1-260x300.jpg',
      color: '#666'
    }
  ];

  const handlePositions = [
    { id: 'left', name: t('options.left') },
    { id: 'right', name: t('options.right') }
  ];

  const thresholdHeights = [
    { id: '30mm', name: '30 mm', value: 30 },
    { id: '55mm', name: '55 mm', value: 55 }
  ];

  const glassTextures = [
    { 
      id: 'clear', 
      name: t('options.clearGlass'),
      description: t('options.clearGlassDesc'),
      image: process.env.PUBLIC_URL + '/images/glass-helder.svg'
    },
    { 
      id: 'chinchilla', 
      name: t('options.chinchilla'),
      description: t('options.chinchillaDesc'),
      image: process.env.PUBLIC_URL + '/images/glass-chinchilla.svg'
    },
    { 
      id: 'satinato', 
      name: t('options.satinato'),
      description: t('options.satinatoDesc'),
      image: process.env.PUBLIC_URL + '/images/glass-satinato.svg'
    }
  ];

  const glazingTypes = [
    { 
      id: 'double-24', 
      name: t('options.doubleGlass24'),
      description: t('options.doubleGlass24Desc'),
      image: process.env.PUBLIC_URL + '/images/hr.png' // Update with actual image path
    },
    { 
      id: 'double-36', 
      name: t('options.doubleGlass36'),
      description: t('options.doubleGlass36Desc'),
      image: process.env.PUBLIC_URL + '/images/hr-1.png' // Update with actual image path
    }
  ];

  const parapetOptions = [
    { id: 'no-parapet', name: t('options.noParapet') },
    { id: 'with-parapet', name: t('options.withParapet') }
  ];

  const securityOptions = [
    { id: 'skg-2', name: t('options.skg2Waiting') }
  ];

  const colors = [
    { id: 'anthracite', name: t('options.anthraciteGrey'), hex: '#3D3D3D' },
    { id: 'black', name: t('options.black'), hex: '#1A1A1A' },
    { id: 'white', name: t('options.white'), hex: '#FFFFFF' },
    { id: 'brown', name: t('options.chocolateBrown'), hex: '#5C4033' },
    { id: 'beige', name: t('options.saharaBeige'), hex: '#C9B896' },
    { id: 'green', name: t('options.forestGreen'), hex: '#2C5F2D' },
    { id: 'blue', name: t('options.steelBlue'), hex: '#4A7C8C' },
    { id: 'wood', name: t('options.goldenOak'), hex: '#A5784C' }
  ];

  return (
    <div className="h-full overflow-y-auto p-6 bg-white border-r border-gray-200">
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h2 className="text-2xl font-bold text-gray-900">{t('options.terraceDoorConfiguration')}</h2>
          <p className="text-sm text-gray-500 mt-1">{t('options.configureTerraceDoor')}</p>
        </div>

      {/* Design Settings */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">{t('options.designSettings')}</CardTitle>
          <CardDescription className="text-xs">{t('options.chooseSidePanelConfiguration')}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-3">
            {designOptions.map((option) => (
              <button
                key={option.id}
                onClick={() => onConfigChange('designType', option.id)}
                className={`relative rounded-lg border-2 transition-all hover:border-orange-400 hover:bg-orange-50 overflow-hidden ${
                  config.designType === option.id
                    ? 'border-orange-500 bg-orange-50 shadow-md'
                    : 'border-gray-200 bg-white'
                }`}
              >
                <div className={`w-full aspect-video flex items-center justify-center p-2 ${
                  config.designType === option.id ? 'bg-white' : 'bg-gray-50'
                }`}>
                  <img
                    src={option.image}
                    alt={option.name}
                    className="max-h-full max-w-full object-contain w-full h-full"
                    style={{
                      display: 'block',
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain'
                    }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
                <div className={`text-xs font-semibold text-center py-2 px-1 ${
                  config.designType === option.id ? 'text-orange-600' : 'text-gray-900'
                }`}>{option.name}</div>
                {option.description && (
                  <div className="text-[10px] text-gray-500 text-center px-1 pb-2">{option.description}</div>
                )}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Specifications */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">{t('options.specifications')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="text-xs text-gray-500 mb-2">
            Range: {MIN_DIMENSION}-{MAX_DIMENSION} mm
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="door-width" className="text-sm font-medium">{t('options.width')}</Label>
              <Input
                id="door-width"
                type="number"
                value={widthInput}
                onChange={(e) => setWidthInput(e.target.value)}
                onBlur={() => commitDimension('width', widthInput)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.target.blur();
                    commitDimension('width', widthInput);
                  }
                }}
                min={MIN_DIMENSION}
                max={MAX_DIMENSION}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="door-height" className="text-sm font-medium">{t('options.height')}</Label>
              <Input
                id="door-height"
                type="number"
                value={heightInput}
                onChange={(e) => setHeightInput(e.target.value)}
                onBlur={() => commitDimension('height', heightInput)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.target.blur();
                    commitDimension('height', heightInput);
                  }
                }}
                min={MIN_DIMENSION}
                max={MAX_DIMENSION}
                className="mt-1"
              />
            </div>
          </div>

          <div>
            <Label className="text-sm font-medium mb-2 block">{t('options.doorType')}</Label>
            <div className="grid grid-cols-2 gap-2">
              {doorTypes.map((type) => (
                <button
                  key={type.id}
                  onClick={() => onConfigChange('doorType', type.id)}
                  className={`p-3 rounded-lg border-2 transition-all hover:border-orange-400 hover:bg-orange-50 text-center ${
                    config.doorType === type.id
                      ? 'border-orange-500 bg-orange-50 shadow-sm'
                      : 'border-gray-200 bg-white'
                  }`}
                >
                  <div className={`text-sm font-medium ${
                    config.doorType === type.id ? 'text-orange-600' : 'text-gray-900'
                  }`}>{type.name}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Overhead Parts - only show when Top Light is selected */}
          {config.doorType === 'top-light' && (
            <div>
              <Label className="text-sm font-medium mb-2 block">{t('options.overheadParts')}</Label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => {
                    onConfigChange('overheadParts', 1);
                    onConfigChange('overheadPartWindowTypes', ['fixed-sash']);
                  }}
                  className={`p-3 rounded-lg border-2 transition-all hover:border-orange-400 hover:bg-orange-50 text-center ${
                    (config.overheadParts || 1) === 1
                      ? 'border-orange-500 bg-orange-50 shadow-sm'
                      : 'border-gray-200 bg-white'
                  }`}
                >
                  <div className={`text-sm font-medium ${
                    (config.overheadParts || 1) === 1 ? 'text-orange-600' : 'text-gray-900'
                  }`}>1 Part</div>
                </button>
                <button
                  onClick={() => {
                    onConfigChange('overheadParts', 2);
                    onConfigChange('overheadPartWindowTypes', ['fixed-sash', 'fixed-sash']);
                  }}
                  className={`p-3 rounded-lg border-2 transition-all hover:border-orange-400 hover:bg-orange-50 text-center ${
                    (config.overheadParts || 1) === 2
                      ? 'border-orange-500 bg-orange-50 shadow-sm'
                      : 'border-gray-200 bg-white'
                  }`}
                >
                  <div className={`text-sm font-medium ${
                    (config.overheadParts || 1) === 2 ? 'text-orange-600' : 'text-gray-900'
                  }`}>2 Parts</div>
                </button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Profile System - Image Based */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">{t('options.profileSystem')}</CardTitle>
          <CardDescription className="text-xs">Select profile type</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-3">
            {profileSystems.map(system => (
              <button
                key={system.id}
                onClick={() => onConfigChange('profileSystem', system.id)}
                className={`p-4 rounded-lg border-2 transition-all hover:border-orange-400 hover:bg-orange-50 ${
                  config.profileSystem === system.id
                    ? 'border-orange-500 bg-orange-50 shadow-md'
                    : 'border-gray-200 bg-white'
                }`}
              >
                <div className={`w-full h-20 mb-2 rounded flex items-center justify-center p-2 ${
                  config.profileSystem === system.id ? 'bg-white' : 'bg-gray-50'
                }`}>
                  <img 
                    src={system.logo} 
                    alt={system.name}
                    className="max-h-full max-w-full object-contain"
                    style={{ 
                      display: 'block',
                      width: 'auto',
                      height: 'auto',
                      maxWidth: '100%',
                      maxHeight: '100%'
                    }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      const fallback = e.target.nextElementSibling;
                      if (fallback) {
                        fallback.style.display = 'flex';
                        fallback.style.alignItems = 'center';
                        fallback.style.justifyContent = 'center';
                      }
                    }}
                  />
                  <div 
                    className="hidden text-xl font-bold"
                    style={{ color: system.color || '#666' }}
                  >
                    {system.name}
                  </div>
                </div>
                <div className="text-xs font-semibold text-gray-900 text-center">{system.name}</div>
                {system.description && (
                  <div className="text-[10px] text-gray-500 text-center mt-0.5">{system.description}</div>
                )}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Handles */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">{t('options.handle')}</CardTitle>
          <CardDescription className="text-xs">
            Do you want the handle viewed from inside in the left part or in the right part?
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-2">
            {handlePositions.map((position) => (
              <button
                key={position.id}
                onClick={() => onConfigChange('handlePosition', position.id)}
                className={`p-3 rounded-lg border-2 transition-all hover:border-orange-400 hover:bg-orange-50 text-center ${
                  config.handlePosition === position.id
                    ? 'border-orange-500 bg-orange-50 shadow-sm'
                    : 'border-gray-200 bg-white'
                }`}
              >
                <div className={`text-sm font-medium ${
                  config.handlePosition === position.id ? 'text-orange-600' : 'text-gray-900'
                }`}>{position.name}</div>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Threshold */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">{t('options.threshold')}</CardTitle>
          <CardDescription className="text-xs">
            Do you want to set the threshold height at 30 mm or 55 mm for the door?
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-2">
            {thresholdHeights.map((height) => (
              <button
                key={height.id}
                onClick={() => onConfigChange('thresholdHeight', height.id)}
                className={`p-3 rounded-lg border-2 transition-all hover:border-orange-400 hover:bg-orange-50 text-center ${
                  config.thresholdHeight === height.id
                    ? 'border-orange-500 bg-orange-50 shadow-sm'
                    : 'border-gray-200 bg-white'
                }`}
              >
                <div className={`text-sm font-medium ${
                  config.thresholdHeight === height.id ? 'text-orange-600' : 'text-gray-900'
                }`}>{height.name}</div>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Wood Joint */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">{t('options.woodJoint')}</CardTitle>
          <CardDescription className="text-xs">Select corner joint type</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-2">
            {woodJointOptions.map(option => (
              <button
                key={option.id}
                onClick={() => onConfigChange('woodJoint', option.id)}
                className={`p-3 rounded-lg border-2 transition-all hover:border-orange-400 hover:bg-orange-50 text-center ${
                  config.woodJoint === option.id
                    ? 'border-orange-500 bg-orange-50 shadow-sm'
                    : 'border-gray-200 bg-white'
                }`}
              >
                <div className={`text-sm font-medium ${
                  config.woodJoint === option.id ? 'text-orange-600' : 'text-gray-900'
                }`}>{translateOptionName(option.id, option.name)}</div>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Glass Texture */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">{t('options.glassTexture')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {glassTextures.map(texture => (
              <button
                key={texture.id}
                onClick={() => onConfigChange('glassTexture', texture.id)}
                className={`w-full p-3 rounded-lg border-2 transition-all hover:border-orange-400 hover:bg-orange-50 text-left flex items-center space-x-3 ${
                  config.glassTexture === texture.id
                    ? 'border-orange-500 bg-orange-50 shadow-sm'
                    : 'border-gray-200 bg-white'
                }`}
              >
                {/* Thumbnail image if available */}
                {texture.image && (
                  <div className="w-16 h-16 flex-shrink-0 rounded bg-gray-50 flex items-center justify-center overflow-hidden">
                    <img
                      src={texture.image}
                      alt={texture.name}
                      className="max-w-full max-h-full object-contain"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                )}
                <div>
                  <div
                    className={`text-sm font-semibold ${
                      config.glassTexture === texture.id ? 'text-orange-600' : 'text-gray-900'
                    }`}
                  >
                    {translateGlassTextureName(texture.id)}
                  </div>
                  {texture.description && (
                    <div className="text-xs text-gray-500 mt-0.5">
                      {translateGlassTextureDescription(texture.id)}
                    </div>
                  )}
                </div>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Glazing Type */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">{t('options.glazingType')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {glazingTypes.map(glazing => (
              <button
                key={glazing.id}
                onClick={() => onConfigChange('glazingType', glazing.id)}
                className={`w-full p-3 rounded-lg border-2 transition-all hover:border-orange-400 hover:bg-orange-50 text-left flex items-center space-x-3 ${
                  config.glazingType === glazing.id
                    ? 'border-orange-500 bg-orange-50 shadow-sm'
                    : 'border-gray-200 bg-white'
                }`}
              >
                {/* Thumbnail image if available */}
                {glazing.image && (
                  <div className="w-16 h-16 flex-shrink-0 rounded bg-gray-50 flex items-center justify-center overflow-hidden">
                    <img
                      src={glazing.image}
                      alt={glazing.name}
                      className="max-w-full max-h-full object-contain"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                )}
                <div>
                  <div
                    className={`text-sm font-semibold ${
                      config.glazingType === glazing.id ? 'text-orange-600' : 'text-gray-900'
                    }`}
                  >
                    {translateGlazingTypeName(glazing.id)}
                  </div>
                  {glazing.description && (
                    <div className="text-xs text-gray-500 mt-0.5">
                      {translateGlazingTypeDescription(glazing.id)}
                    </div>
                  )}
                </div>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Parapet */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">{t('options.parapet')}</CardTitle>
          <CardDescription className="text-xs">
            Do you want to apply so-called parapet for this terrace door?
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-2">
            {parapetOptions.map((option) => (
              <button
                key={option.id}
                onClick={() => onConfigChange('parapet', option.id)}
                className={`p-3 rounded-lg border-2 transition-all hover:border-orange-400 hover:bg-orange-50 text-center ${
                  config.parapet === option.id
                    ? 'border-orange-500 bg-orange-50 shadow-sm'
                    : 'border-gray-200 bg-white'
                }`}
              >
                <div className={`text-sm font-medium ${
                  config.parapet === option.id ? 'text-orange-600' : 'text-gray-900'
                }`}>{option.name}</div>
              </button>
            ))}
          </div>

          {config.parapet === 'with-parapet' && (
            <div className="mt-4 space-y-4">
              <div>
                <Label htmlFor="parapet-height" className="text-sm font-medium">
                  Parapet Height (mm)
                </Label>
                <CardDescription className="text-xs mt-0.5 mb-2">
                  Measured from the bottom of the frame to the center of the intermediate post
                </CardDescription>
                <Input
                  id="parapet-height"
                  type="number"
                  value={config.parapetHeight || 250}
                  onChange={(e) => {
                    const value = parseInt(e.target.value) || 250;
                    // Clamp between 0 and door height (same as sub measurement input)
                    const clampedValue = Math.min(config.height || 2100, Math.max(0, value));
                    onConfigChange('parapetHeight', clampedValue);
                  }}
                  min={0}
                  max={config.height || 2100}
                  className="mt-1"
                />
              </div>

              {/* Parapet Color */}
              <div>
                <Label className="text-sm font-medium mb-2 block">
                  Parapet Color
                </Label>
                <CardDescription className="text-xs mb-2">
                  Choose a color for the parapet panel
                </CardDescription>
                <ColorPicker
                  selectedColor={config.parapetColor}
                  onColorChange={(colorId) => onConfigChange('parapetColor', colorId)}
                  showSearch={true}
                  maxVisible={8}
                />
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Lintels */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">{t('options.lintels')}</CardTitle>
          <CardDescription className="text-xs">
            Do you want lintels for this terrace door?
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={() => onConfigChange('lintels', false)}
              className={`p-3 rounded-lg border-2 transition-all hover:border-orange-400 hover:bg-orange-50 text-center ${
                !config.lintels
                  ? 'border-orange-500 bg-orange-50 shadow-sm'
                  : 'border-gray-200 bg-white'
              }`}
            >
              <div className={`text-sm font-medium ${
                !config.lintels ? 'text-orange-600' : 'text-gray-900'
              }`}>{t('options.no')}</div>
            </button>
            <button
              onClick={() => onConfigChange('lintels', true)}
              className={`p-3 rounded-lg border-2 transition-all hover:border-orange-400 hover:bg-orange-50 text-center ${
                config.lintels
                  ? 'border-orange-500 bg-orange-50 shadow-sm'
                  : 'border-gray-200 bg-white'
              }`}
            >
              <div className={`text-sm font-medium ${
                config.lintels ? 'text-orange-600' : 'text-gray-900'
              }`}>{t('options.yes')}</div>
            </button>
          </div>
        </CardContent>
      </Card>

      {/* Security Bracket */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">{t('options.security')}</CardTitle>
          <CardDescription className="text-xs">
            Would you like your frame provided with extra security bracket?
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Select
            value={config.securityBracket || 'none'}
            onValueChange={(value) => onConfigChange('securityBracket', value)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">None</SelectItem>
              {securityOptions.map((option) => (
                <SelectItem key={option.id} value={option.id}>
                  {option.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Interior Color */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">{t('options.interiorColor')}</CardTitle>
          <CardDescription className="text-xs">Select from standard colors or search RAL codes</CardDescription>
        </CardHeader>
        <CardContent>
          <ColorPicker
            selectedColor={config.interiorColor}
            onColorChange={(colorId) => onConfigChange('interiorColor', colorId)}
            showSearch={true}
            maxVisible={8}
          />
        </CardContent>
      </Card>

      {/* Exterior Frame Color */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">{t('options.exteriorColor')}</CardTitle>
          <CardDescription className="text-xs">Select from standard colors or search RAL codes</CardDescription>
        </CardHeader>
        <CardContent>
          <ColorPicker
            selectedColor={config.exteriorColor}
            onColorChange={(colorId) => onConfigChange('exteriorColor', colorId)}
            showSearch={true}
            maxVisible={8}
          />
        </CardContent>
      </Card>
      </div>
    </div>
  );
};

export default TerrasdeurOptionsPanel;

